import type { DirectMessagingCcdaHtmlResponse } from '@dev-console/direct-messaging';
import type { SyntheticEvent } from 'react';
import { useEffect, useState } from 'react';

import { api } from '@/config/api';

type RenderState = { status: 'loading' } | { status: 'error'; message: string } | { status: 'ready'; html: string };

function blockExternalPreviewLink(event: Event): void {
  const target = event.target instanceof Element ? event.target : null;
  const href = target?.closest('a')?.getAttribute('href');
  if (href && !href.startsWith('#')) event.preventDefault();
}

function secureRenderedDocument(event: SyntheticEvent<HTMLIFrameElement>): void {
  event.currentTarget.contentDocument?.addEventListener('click', blockExternalPreviewLink);
}

export function CcdaDocumentView({ xml }: { xml: string }) {
  const [renderState, setRenderState] = useState<RenderState>({ status: 'loading' });

  useEffect(() => {
    let current = true;
    setRenderState({ status: 'loading' });
    api<DirectMessagingCcdaHtmlResponse>('/dev-console/direct-messaging/ccda-html', {
      method: 'POST',
      body: JSON.stringify({ xml }),
    })
      .then((response) => {
        if (!current) return;
        setRenderState(
          response.html == null
            ? { status: 'error', message: response.error ?? 'No C-CDA view is available for this XML.' }
            : { status: 'ready', html: response.html }
        );
      })
      .catch(() => {
        if (current) setRenderState({ status: 'error', message: 'The HL7 rendering could not be loaded.' });
      });
    return () => {
      current = false;
    };
  }, [xml]);

  if (renderState.status === 'error') {
    return <div className='rounded-md bg-neutral-50 p-4 text-sm text-neutral-600'>{renderState.message}</div>;
  }

  if (renderState.status === 'loading') {
    return (
      <div className='rounded-md bg-neutral-50 p-4 text-sm text-neutral-600'>Preparing the HL7 CDA rendering…</div>
    );
  }

  return (
    <div className='space-y-3'>
      <div className='rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800'>
        Rendered on the server with the official HL7 CDA reference stylesheet, the same renderer Jarvis uses for
        referrals. This approximates a standards-based EHR view, but receiving systems may apply their own renderer.
        Scripts and external media are disabled.
      </div>
      <iframe
        className='h-[900px] w-full rounded-md border border-neutral-200 bg-white'
        onLoad={secureRenderedDocument}
        referrerPolicy='no-referrer'
        sandbox='allow-same-origin'
        srcDoc={renderState.html}
        title='Official HL7 CDA document rendering'
      />
    </div>
  );
}
